import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { config } from '@uhg-abyss/web/tools/config';
import { event } from '@uhg-abyss/web/tools/event';
import { parseByTag, parseTagNames } from '../useDocumentationV3/parsers';
import SwaggerParser from '@apidevtools/swagger-parser';

const useAction = (actionFn) => {
  const dispatch = useDispatch();
  return useCallback(
    (...args) => {
      return dispatch(actionFn(...args));
    },
    [dispatch]
  );
};

export const ACTION_TYPE = 'GET_DOCUMENTATION_V3';

/**
 * Services that require manual allOf flattening and $ref resolution
 * (e.g., FHIR-based APIs with circular schemas).
 */
const SERVICES_REQUIRING_FLATTEN = ['provideraccess'];

/**
 * Check if the given service name needs manual schema flattening.
 */
const needsFlatten = (service) => {
  return SERVICES_REQUIRING_FLATTEN.some((s) => {
    return service.startsWith(s);
  });
};

/**
 * Resolve a JSON Pointer $ref against the root spec.
 */
const resolveRef = (ref, root) => {
  if (!ref || typeof ref !== 'string' || !ref.startsWith('#/')) return null;
  const parts = ref.replace('#/', '').split('/');
  let result = root;
  for (const part of parts) {
    result = result?.[part];
    if (result === undefined) return null;
  }
  return result;
};

/**
 * Extract the schema name from a $ref string.
 * e.g., "#/components/schemas/Patient" -> "Patient"
 */
const getSchemaNameFromRef = (ref) => {
  if (!ref || typeof ref !== 'string') return 'object';
  const parts = ref.split('/');
  return parts[parts.length - 1] || 'object';
};

/**
 * Flatten all allOf entries into a single schema with merged properties,
 * required arrays, and description. Handles nested allOf within allOf.
 *
 * @param {object} schema - The schema to flatten
 * @param {object} root - The root spec object for resolving $refs
 * @param {Set} seen - Set of $ref strings already visited in this path
 * @param {number} maxDepth - Maximum recursion depth for circular refs
 * @param {number} currentDepth - Current recursion depth
 */
const flattenAllOf = (
  schema,
  root,
  seen = new Set(),
  maxDepth = 2,
  currentDepth = 0
) => {
  if (!schema || typeof schema !== 'object') return schema;

  // Resolve $ref first if present
  if (schema.$ref) {
    const schemaName = getSchemaNameFromRef(schema.$ref);

    if (seen.has(schema.$ref)) {
      // We've seen this $ref before in the current path — it's circular.
      // Allow one more level of expansion before stopping.
      if (currentDepth < maxDepth) {
        const resolved = resolveRef(schema.$ref, root);
        if (resolved) {
          // Expand one more level but with incremented depth
          const expandedSeen = new Set(seen);
          const flattened = flattenAllOf(
            resolved,
            root,
            expandedSeen,
            maxDepth,
            currentDepth + 1
          );
          if (!flattened.title) {
            flattened.title = schemaName;
          }
          return flattened;
        }
      }

      // Reached max depth — stop with a circular marker
      return {
        type: schemaName,
        title: schemaName,
        description: `(circular reference to ${schemaName})`,
        'x-circular': true,
      };
    }

    seen.add(schema.$ref);
    const resolved = resolveRef(schema.$ref, root);
    if (resolved) {
      const flattened = flattenAllOf(
        resolved,
        root,
        seen,
        maxDepth,
        currentDepth
      );
      if (!flattened.title) {
        flattened.title = schemaName;
      }
      return flattened;
    }
    return schema;
  }

  if (!schema.allOf) {
    // Still process nested properties that may contain allOf/$ref
    return resolveSchemaProperties(schema, root, seen, maxDepth, currentDepth);
  }

  const merged = {
    type: 'object',
    properties: {},
    required: [],
  };

  // Copy top-level fields from the schema itself (description, title, etc.)
  if (schema.description) merged.description = schema.description;
  if (schema.title) merged.title = schema.title;
  if (schema.type) merged.type = schema.type;

  // Also merge any properties defined at the same level as allOf
  if (schema.properties) {
    Object.entries(schema.properties).forEach(([key, value]) => {
      merged.properties[key] = flattenAllOf(
        value,
        root,
        new Set(seen),
        maxDepth,
        currentDepth
      );
    });
  }
  if (schema.required) {
    merged.required = [...merged.required, ...schema.required];
  }

  // Flatten each allOf entry
  schema.allOf.forEach((subSchema) => {
    const flattened = flattenAllOf(
      subSchema,
      root,
      new Set(seen),
      maxDepth,
      currentDepth
    );

    if (flattened.properties) {
      Object.entries(flattened.properties).forEach(([key, value]) => {
        merged.properties[key] = flattenAllOf(
          value,
          root,
          new Set(seen),
          maxDepth,
          currentDepth
        );
      });
    }
    if (flattened.required) {
      merged.required = [...merged.required, ...flattened.required];
    }
    if (flattened.description && !merged.description) {
      merged.description = flattened.description;
    }
    if (flattened.title && !merged.title) {
      merged.title = flattened.title;
    }
    if (flattened.type) {
      merged.type = flattened.type;
    }
  });

  // Deduplicate required
  merged.required = [...new Set(merged.required)];
  if (merged.required.length === 0) delete merged.required;
  if (Object.keys(merged.properties).length === 0) delete merged.properties;

  return merged;
};

/**
 * Recursively resolve $ref and allOf within schema properties, items, etc.
 */
const resolveSchemaProperties = (
  schema,
  root,
  seen = new Set(),
  maxDepth = 2,
  currentDepth = 0
) => {
  if (!schema || typeof schema !== 'object') return schema;

  const result = { ...schema };

  // Resolve properties
  if (result.properties) {
    const resolvedProps = {};
    Object.entries(result.properties).forEach(([key, value]) => {
      resolvedProps[key] = flattenAllOf(
        value,
        root,
        new Set(seen),
        maxDepth,
        currentDepth
      );
    });
    result.properties = resolvedProps;
  }

  // Resolve items (for arrays)
  if (result.items) {
    const resolvedItems = flattenAllOf(
      result.items,
      root,
      new Set(seen),
      maxDepth,
      currentDepth
    );
    result.items = resolvedItems;
    // If the items resolved to a named schema, preserve the type hint
    if (resolvedItems.title && result.type === 'array') {
      result['x-item-type'] = resolvedItems.title;
    }
  }

  // Resolve additionalProperties
  if (
    result.additionalProperties &&
    typeof result.additionalProperties === 'object'
  ) {
    result.additionalProperties = flattenAllOf(
      result.additionalProperties,
      root,
      new Set(seen),
      maxDepth,
      currentDepth
    );
  }

  return result;
};

/**
 * Walk every operation in the spec and flatten allOf / resolve $refs
 * in requestBody and response schemas.
 */
const flattenSpec = (spec) => {
  if (!spec?.paths) return spec;

  const methods = ['get', 'post', 'put', 'patch', 'delete', 'options', 'head'];

  Object.values(spec.paths).forEach((pathItem) => {
    methods.forEach((method) => {
      const operation = pathItem[method];
      if (!operation) return;

      // Flatten requestBody schemas
      if (operation.requestBody?.content) {
        Object.keys(operation.requestBody.content).forEach((contentType) => {
          const mediaType = operation.requestBody.content[contentType];
          if (mediaType?.schema) {
            mediaType.schema = flattenAllOf(
              mediaType.schema,
              spec,
              new Set(),
              2,
              0
            );
          }
        });
      }

      // Flatten response schemas
      if (operation.responses) {
        Object.values(operation.responses).forEach((response) => {
          if (response?.content) {
            Object.keys(response.content).forEach((contentType) => {
              const mediaType = response.content[contentType];
              if (mediaType?.schema) {
                mediaType.schema = flattenAllOf(
                  mediaType.schema,
                  spec,
                  new Set(),
                  2,
                  0
                );
              }
            });
          }
        });
      }

      // Flatten parameter schemas
      if (operation.parameters) {
        operation.parameters.forEach((param, index) => {
          if (param?.schema) {
            operation.parameters[index].schema = flattenAllOf(
              param.schema,
              spec,
              new Set(),
              2,
              0
            );
          }
        });
      }
    });
  });

  return spec;
};

export const useDocumentationV3 = (options) => {
  const result = useSelector((state) => {
    return state[ACTION_TYPE];
  });
  const action = useAction((args) => {
    const eapm = window.elasticApm;
    if (eapm) {
      window.elasticApm.startTransaction(ACTION_TYPE, 'http-request', {
        managed: true,
      });
    }
    const { service } = args;
    let documentationApiUrl = service.startsWith('superconnector')
      ? config('DOCUMENTATION_API_URL_SC')
      : config('DOCUMENTATION_API_URL');

    return (dispatch) => {
      dispatch({ type: `${ACTION_TYPE}_CALLED` });

      const parsePromise = needsFlatten(service)
        ? // FHIR / circular schemas: bundle only, then manually flatten
          SwaggerParser.bundle(`${documentationApiUrl}${service}.json`).then(
            (bundled) => {
              return flattenSpec(bundled);
            }
          )
        : // Standard APIs: full dereference is safe
          SwaggerParser.dereference(`${documentationApiUrl}${service}.json`, {
            dereference: {
              circular: 'ignore',
            },
          });

      parsePromise
        .then((res) => {
          event('API_TRANSACTION', {
            fields: {
              appVersion: process.env.PACKAGE_VERSION,
              url: window.location.href,
              endpoint: config('DOCUMENTATION_API_URL'),
              userInfo: args?.userInfo,
              method: 'GET',
              code: res.status,
              operationName: ACTION_TYPE,
              message: res.statusText,
            },
          });
          return res;
        })
        .then((res) => {
          const s = res;
          const pathsByTag = parseByTag(s, service);
          const tagNames = parseTagNames(pathsByTag);
          const data = {
            ...s,
            pathsByTag,
            tagNames,
            service,
          };

          dispatch({
            type: `${ACTION_TYPE}_COMPLETED`,
            payload: data,
          });
          if (options && options.onCompleted) {
            options.onCompleted(data);
          }
        })
        .catch((error) => {
          dispatch({
            type: `${ACTION_TYPE}_ERROR`,
            payload: error,
          });
          if (options && options.onError) {
            options.onError(error);
          }
        });
    };
  });

  return [result, action];
};
